import { cx } from '../../design/index.js';
import { SiteSection } from '../SiteSection.js';

/**
 * What happens when nobody picks up (§18), as the call's own log.
 *
 * The same call as the diary at the top of the page — Saturday, 11:02 — told
 * minute by minute. The marker of each step is the time it happened rather
 * than a number, because a salon reads its day by the clock: the sequence is
 * real, and the time is the most useful way to show it.
 *
 * The dot says who has the call: amber while it rings with nobody to answer,
 * teal from the moment GSI Voice takes it, as in the diary. It is never the
 * only signal — each step says the same thing in words.
 *
 * One ordered list, which is what it is, so a screen reader reads a sequence
 * rather than six unrelated blocks.
 */

const STEPS: readonly {
  readonly time: string;
  readonly title: string;
  readonly detail: string;
  readonly taken: boolean;
}[] = [
  {
    time: '11 h 02',
    title: 'Le client appelle',
    detail: 'Un samedi matin, pendant une couleur.',
    taken: false,
  },
  {
    time: '11 h 02',
    title: 'Personne ne décroche',
    detail: 'Vous avez les mains prises. Cela arrive tous les jours.',
    taken: false,
  },
  {
    time: '11 h 02',
    title: 'GSI Voice prend l’appel',
    detail: 'Au bout de quelques sonneries, sans que vous fassiez rien.',
    taken: true,
  },
  {
    time: '11 h 03',
    title: 'L’agenda est consulté',
    detail: 'Les vraies disponibilités, pas une estimation.',
    taken: true,
  },
  {
    time: '11 h 03',
    title: 'Le rendez-vous est posé',
    detail: 'La bonne prestation, avec le bon collaborateur.',
    taken: true,
  },
  {
    time: '11 h 04',
    title: 'Le client reçoit son SMS',
    detail: 'La confirmation part tout de suite.',
    taken: true,
  },
];

export function MissedCall(): React.JSX.Element {
  return (
    <SiteSection
      id="fonctionnement"
      title="Quand vous ne décrochez pas."
      intro={
        <p>
          Aujourd’hui, l’appel sonne dans le vide et le client appelle le salon suivant. Avec GSI
          Voice, le même appel se passe ainsi.
        </p>
      }
    >
      <ol className="border-b border-wall-line">
        {STEPS.map((step) => (
          <li
            key={step.title}
            className="grid grid-cols-[4.75rem_1fr] items-baseline gap-x-4 border-t border-wall-line py-5 sm:grid-cols-[7rem_1fr] sm:gap-x-8 sm:py-6"
          >
            <span className="sign-figure text-subtitle text-ink sm:text-sign-sm">{step.time}</span>
            <span className="min-w-0">
              <span className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className={cx(
                    'h-2 w-2 shrink-0 rounded-pill',
                    step.taken ? 'bg-teal-ink' : 'bg-ringing',
                  )}
                />
                <span className="text-subtitle font-medium text-ink">{step.title}</span>
              </span>
              <span className="mt-1 block pl-[1.125rem] text-ui text-ink-muted">{step.detail}</span>
            </span>
          </li>
        ))}
      </ol>
    </SiteSection>
  );
}
